import React, { type ReactNode } from 'react'
import { DecoratorNode, type NodeKey, type LexicalNode, type LexicalEditor, type SerializedLexicalNode } from 'lexical'
import classNames from 'classnames'

export type ReferenceType = 'normal' | 'warning'

export type SerializedReferenceNode = SerializedLexicalNode & {
    type: 'reference'
    number: number
    referenceType: ReferenceType
    isWarningVisible: boolean
}

export function isSerializedReferenceNode(node: SerializedLexicalNode): node is SerializedReferenceNode {
    return node.type === 'reference'
}

export class ReferenceNode extends DecoratorNode<ReactNode> {
    __number: number
    __referenceType: ReferenceType
    __isWarningVisible: boolean

    static getType(): string {
        return 'reference'
    }

    isWarningVisible() {
        return this.__isWarningVisible
    }

    referenceType() {
        return this.__referenceType
    }

    static clone(node: ReferenceNode): ReferenceNode {
        return new ReferenceNode(node.__number, node.__referenceType, node.__isWarningVisible, node.__key)
    }

    static importJSON(serializedNode: SerializedReferenceNode) {
        return new ReferenceNode(serializedNode.number, serializedNode.referenceType, serializedNode.isWarningVisible)
    }

    constructor(number: number, referenceType: ReferenceType, showWarning: boolean, key?: NodeKey) {
        super(key)
        this.__number = number
        this.__referenceType = referenceType
        this.__isWarningVisible = showWarning
    }

    createDOM(): HTMLElement {
        return document.createElement('span')
    }

    updateDOM(): false {
        return false
    }

    decorate(_editor: LexicalEditor): ReactNode {
        return (
            <Reference
                number={this.__number}
                referenceType={this.__referenceType}
                isWarningVisible={this.__isWarningVisible}
            />
        )
    }

    exportJSON(): SerializedReferenceNode {
        return {
            version: 0,
            type: 'reference',
            number: this.__number,
            referenceType: this.__referenceType,
            isWarningVisible: this.__isWarningVisible,
        }
    }

    isIsolated() {
        return true
    }
}

export function $createReferenceNode(
    number: number,
    referenceType: ReferenceType,
    isWarningVisible: boolean
): ReferenceNode {
    return new ReferenceNode(number, referenceType, isWarningVisible)
}

export function $isReferenceNode(node: LexicalNode | null | undefined): node is ReferenceNode {
    return node instanceof ReferenceNode
}

export function Reference({
    number,
    referenceType,
    isWarningVisible,
}: {
    number: number
    referenceType: ReferenceType
    isWarningVisible: boolean
}) {
    return (
        <>
            <span className={classNames('ZapBot_answer_annotation-reference', referenceType)}>{number}</span>
            {isWarningVisible && (
                <span className="ZapBot_answer_warning-icon">
                    <zaptic-icon icon-name="warning-triangle"></zaptic-icon>
                </span>
            )}
        </>
    )
}
